import { computed, getCurrentInstance } from 'vue';
import { isLinkMode } from '@/utils/api';
import { useFlowX } from '@rebuild/hooks/useFlowX';
import i18n from '@/locales';
import { resolveStoreId } from '@rebuild/utils/context';
import { CACHE_IR_VIRTUAL_ID } from '@/const/host';
import { useMenuTree } from './useMenuTree';
import { useStore } from '@/utils/vue-api';

export function useNavigation() {
	const vm = getCurrentInstance();

	const store = useStore();
	const isMobile = computed(() => store.getters.isMobileMode);

	const currentTemplate = computed(() => {
		const tmplParam = vm.proxy.$route.params.tmpl;
		if (tmplParam) return tmplParam;
		// 默认路径识别为 flow 类型
		return 'flow';
	});

	// 检测当前路径风格（默认风格 vs 显式风格）
	const isDefaultStyle = computed(() => {
		const tmplParam = vm.proxy.$route.params.tmpl;
		if (tmplParam) return false;
		return true;
	});

	// 构建模板路径的工具函数
	const buildTemplatePath = (page, templateType = null) => {
		const prefix = '/investors';
		const targetTemplate = templateType || currentTemplate.value;

		// 如果目标模板类型是 flow，可以使用两种风格
		if (targetTemplate === 'flow') {
			return isDefaultStyle.value ? `${prefix}/${page}` : `${prefix}/flow/${page}`;
		}

		const tmplId = vm.proxy.$route.params.tmplId;
		if (tmplId) {
			return `${prefix}/${targetTemplate}/${tmplId}/${page}`;
		}

		// 其他模板类型使用显式风格
		return `${prefix}/${targetTemplate}/${page}`;
	};

	const buildTemplateQuery = (query = {}) => {
		const mergeQuery = {
			...query,
			lang: i18n.locale
		};

		const staticStoreId = sessionStorage.getItem(CACHE_IR_VIRTUAL_ID);
		if (!staticStoreId) {
			mergeQuery.storeId = resolveStoreId();
		}

		const modeInUrl = vm.proxy.$route.query.mode;
		if (modeInUrl) {
			mergeQuery.mode = modeInUrl;
		}

		const versionInUrl = vm.proxy.$route.query.devVersion;
		if (versionInUrl) {
			mergeQuery.devVersion = versionInUrl;
		}

		return mergeQuery;
	};

	// 导航方法 - 保持当前路径风格
	const navigateToPage = (page, query = {}) => {
		const path = buildTemplatePath(page);
		console.log('😪 # navigateToPage # path:', path);
		vm.proxy.$router.push({ path, query: buildTemplateQuery(query) });
	};
	const replaceToPage = (page, query = {}) => {
		const path = buildTemplatePath(page);
		console.log('😪 # replaceToPage # path:', path);
		vm.proxy.$router.replace({ path, query: buildTemplateQuery(query) });
	};

	const goToHome = (query = {}) => navigateToPage('index', query);
	const goToModule = (moduleId, query) => navigateToPage(moduleId, query);
	const goToModuleSubPath = (moduleId, subPath, query) => navigateToPage(`${moduleId}/${subPath}`, query);
	const buildMenuPath = (menuId, moduleId) => {
		let path = 'mme/' + menuId;
		if (moduleId) {
			path += '/' + moduleId;
		}
		return path;
	};
	const gotoMenu = (menuId, moduleId, query) => {
		navigateToPage(buildMenuPath(menuId, moduleId), query);
	};
	const goToPage = (menuId, moduleId, query) => {
		let path = 'page/' + menuId;
		if (moduleId) {
			path += '/' + moduleId;
		}
		navigateToPage(path, query);
	};

	const replaceHome = (query = {}) => replaceToPage('index', query);

	// 特定模板跳转：瀑布式
	const viewModuleMoreInFlow = (menu, module = null) => {
		if (menu.id === 'home') {
			return goToHome();
		}
		const menuId = menu.id;
		const moduleId = module?.moduleId;
		gotoMenu(menuId, moduleId);
	};

	// 特定模板跳转：新版瀑布式
	const { findAnotherMenu } = useFlowX();
	const viewModuleMoreInFlowX = (menu, module = null) => {
		if (module.skipUrl) {
			return window.open(module.skipUrl);
		}
		if (module.jumpLink) {
			return window.open(module.jumpLink);
		}

		let activedMenuId = menu.id;
		const currentModuleId = module?.moduleId;

		if (!isLinkMode()) {
			const anotherMenu = findAnotherMenu(menu, currentModuleId);
			activedMenuId = anotherMenu?.id;
		}
		gotoMenu(activedMenuId, currentModuleId);
	};
	const viewMenuInFlowX = menu => {
		if (menu.isHome) {
			return goToHome();
		}
		const findModuleId = menu?.moduleVOS?.[0].moduleId;
		gotoMenu(menu.id, findModuleId);
	};

	const { getMenuUuidsByModuleId, getMenuByUuid } = useMenuTree();
	const viewPageInSurf = ({ menu = null, module = null, query = {} }) => {
		const targetMenuId = menu?.uuid || vm.proxy.$route.params.menuId;
		if (!targetMenuId) {
			console.warn('viewPageInSurf # targetMenuId is required');
			return;
		}

		const targetMenuUuidList = getMenuUuidsByModuleId(module?.moduleId);
		let realMenuId = targetMenuId;
		if (targetMenuUuidList && targetMenuUuidList.length > 1) {
			// 在 targetMenuUuidList 中找到第一个详情菜单的 uuid
			const matchedMenuUuid = targetMenuUuidList.find(uuid => {
				const menu = getMenuByUuid(uuid);

				if (menu && menu.meta) {
					const isModuleMenu = menu.meta.menuType === 1; // 组件型菜单
					if (isModuleMenu) {
						const topMenu = getMenuByUuid(menu.meta.parentUuid);
						return topMenu?.meta?.displayMode === 1;
					} else {
						return menu.meta.displayMode === 1;
					}
				}

				return null;
			});
			realMenuId = matchedMenuUuid || targetMenuId;
		}

		let __query;
		if (isMobile.value) {
			__query = {
				...query,
				r: new Date().getTime()
			};
		} else {
			__query = query;
		}
		goToPage(realMenuId, module?.moduleId, __query);
	};

	return {
		currentTemplate,

		// 方法
		buildTemplatePath,
		buildTemplateQuery,
		buildMenuPath,
		navigateToPage,

		// 便捷导航方法
		goToHome,
		goToModule,
		goToModuleSubPath,
		gotoMenu,
		goToPage,
		replaceHome,

		viewModuleMoreInFlow,
		viewModuleMoreInFlowX,
		viewMenuInFlowX,
		viewPageInSurf
	};
}
